import type { CaptureSequence, CustomLandmarks, Landmark } from '@/components/posture-analysis/types';

export type FloorCorrectionSource = 'manual' | 'gyro' | 'landmarks' | 'opencv' | 'none';
export type FloorCorrectionWarning = 'sensor-disagreement';

export interface FloorCorrectionResult {
  correctionDeg: number;
  source: FloorCorrectionSource;
  confidence: number;
  pivot: { x: number; y: number } | null;
  warning?: FloorCorrectionWarning;
}

const MIN_PAIR_LENGTH_PX = 16;

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Floor direction is axial (0deg and 180deg are the same line), so normalize to [-90, 90]. */
export const normalizeFloorAngleDeg = (angleDeg: number): number => {
  let normalized = angleDeg;
  while (normalized > 90) normalized -= 180;
  while (normalized <= -90) normalized += 180;
  return normalized;
};

/** Manual plumb: user places two floor points; line angle drives visual plumb only (4B scope). */
export const resolveManualFloorCorrectionFromPoints = (
  first: { x: number; y: number },
  second: { x: number; y: number }
): FloorCorrectionResult | null => {
  const dx = second.x - first.x;
  const dy = second.y - first.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length < MIN_PAIR_LENGTH_PX) return null;
  const floorAngleDeg = normalizeFloorAngleDeg((Math.atan2(dy, dx) * 180) / Math.PI);
  return {
    correctionDeg: -floorAngleDeg,
    source: 'manual',
    confidence: 1,
    pivot: {
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2,
    },
  };
};

/** Rotate a point around a pivot for display/report drawing (manual plumb angle). */
export const rotatePoint = (
  point: { x: number; y: number } | undefined,
  correctionDeg: number,
  pivot: { x: number; y: number } = { x: 0, y: 0 }
): { x: number; y: number } | undefined => {
  if (!point) return undefined;
  if (!isFiniteNumber(correctionDeg) || Math.abs(correctionDeg) < 0.0001) return { x: point.x, y: point.y };
  const rad = (correctionDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

export const rotateLandmarks = (
  landmarks: Landmark[] | null | undefined,
  correctionDeg: number,
  pivot: { x: number; y: number } = { x: 0, y: 0 }
): Landmark[] => {
  if (!landmarks || landmarks.length === 0) return [];
  if (!isFiniteNumber(correctionDeg) || Math.abs(correctionDeg) < 0.0001) {
    return landmarks.map((lm) => (lm ? { ...lm } : lm));
  }

  const rad = (correctionDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  return landmarks.map((lm) => {
    if (!lm) return lm;
    const dx = lm.x - pivot.x;
    const dy = lm.y - pivot.y;
    return {
      ...lm,
      x: pivot.x + dx * cos - dy * sin,
      y: pivot.y + dx * sin + dy * cos,
    };
  });
};

export const rotateCustomLandmarks = (
  custom: CustomLandmarks | undefined,
  correctionDeg: number,
  pivot: { x: number; y: number } = { x: 0, y: 0 }
): CustomLandmarks | undefined => {
  if (!custom) return custom;
  return {
    ASIS: rotatePoint(custom.ASIS, correctionDeg, pivot),
    PSIS: rotatePoint(custom.PSIS, correctionDeg, pivot),
    L_ASIS: rotatePoint(custom.L_ASIS, correctionDeg, pivot),
    R_ASIS: rotatePoint(custom.R_ASIS, correctionDeg, pivot),
    L_PSIS: rotatePoint(custom.L_PSIS, correctionDeg, pivot),
    R_PSIS: rotatePoint(custom.R_PSIS, correctionDeg, pivot),
  };
};
